import { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AppBar,
  Toolbar,
  Container,
  Box,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Collapse,
  useScrollTrigger,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { LanguageSwitcher, GradientButton } from '../components/ui';

const StyledAppBar = styled(AppBar, {
  shouldForwardProp: (prop) => !['scrolled', 'transparent'].includes(prop),
})(({ theme, scrolled, transparent }) => ({
  position: 'fixed',
  background: scrolled
    ? 'rgba(255, 255, 255, 0.98)'
    : transparent
    ? 'transparent'
    : 'rgba(255, 255, 255, 0.98)',
  backdropFilter: scrolled ? 'blur(20px)' : 'none',
  boxShadow: scrolled ? '0 2px 20px rgba(0, 0, 0, 0.08)' : 'none',
  transition: 'all 0.3s ease',
  color: scrolled || !transparent ? theme.palette.text.primary : '#FFFFFF',
}));

const Logo = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  textDecoration: 'none',

  '& img': {
    height: '32px',
    width: 'auto',

    [theme.breakpoints.up('sm')]: {
      height: '36px',
    },

    [theme.breakpoints.up('md')]: {
      height: '40px',
    },
  },
}));

// The nav sits in a soft rail; a single indicator glides to whichever item is
// hovered and settles back on the current page when the pointer leaves.
const NavRail = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'light',
})(({ light }) => ({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  gap: '2px',
  padding: '4px',
  borderRadius: '999px',
  background: light ? 'rgba(255, 255, 255, 0.10)' : 'rgba(26, 41, 66, 0.045)',
  border: `1px solid ${light ? 'rgba(255, 255, 255, 0.18)' : 'rgba(26, 41, 66, 0.06)'}`,
  backdropFilter: light ? 'blur(8px)' : 'none',
  transition: 'background 0.3s ease, border-color 0.3s ease',
}));

const NavIndicator = styled(Box, {
  shouldForwardProp: (prop) => !['light', 'visible'].includes(prop),
})(({ light, visible }) => ({
  position: 'absolute',
  top: '4px',
  bottom: '4px',
  left: 0,
  borderRadius: '999px',
  background: light ? 'rgba(255, 255, 255, 0.22)' : '#FFFFFF',
  boxShadow: light
    ? 'none'
    : '0 1px 2px rgba(26, 41, 66, 0.10), 0 6px 16px rgba(26, 41, 66, 0.06)',
  opacity: visible ? 1 : 0,
  pointerEvents: 'none',
  transition: 'transform 0.35s cubic-bezier(0.4, 0, 0.2, 1), width 0.35s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.2s ease, background 0.3s ease',

  '@media (prefers-reduced-motion: reduce)': {
    transition: 'opacity 0.2s ease',
  },
}));

const NavLink = styled(Button, {
  shouldForwardProp: (prop) => !['active', 'light'].includes(prop),
})(({ theme, active, light }) => ({
  fontFamily: '"Plus Jakarta Sans", sans-serif',
  fontWeight: active ? 700 : 600,
  fontSize: '0.875rem',
  letterSpacing: '0.01em',
  padding: '8px 18px',
  borderRadius: '999px',
  textTransform: 'none',
  position: 'relative',
  zIndex: 1,
  minWidth: 'auto',
  color: light
    ? active ? '#FFFFFF' : 'rgba(255, 255, 255, 0.75)'
    : active ? theme.palette.text.primary : theme.palette.text.secondary,
  transition: 'color 0.25s ease',

  '&:hover': {
    backgroundColor: 'transparent',
    color: light ? '#FFFFFF' : theme.palette.text.primary,
  },

  '&.Mui-focusVisible': {
    outline: `2px solid ${light ? '#FFFFFF' : theme.palette.primary.main}`,
    outlineOffset: '2px',
  },
}));

const MobileDrawer = styled(Drawer)(({ theme }) => ({
  '& .MuiDrawer-paper': {
    width: '100%',
    maxWidth: '320px',
    padding: theme.spacing(2),
    backgroundColor: '#FFFFFF',
  },
}));

const DrawerHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(1, 0, 2, 0),
  borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
  marginBottom: theme.spacing(2),
}));

const navItems = [
  { path: '/', label: 'nav.home' },
  { path: '/about', label: 'nav.about' },
  { path: '/services', label: 'nav.services' },
  { path: '/products', label: 'nav.products' },
  { path: '/contact', label: 'nav.contact' },
];

export default function Header({ transparent = false }) {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [indicator, setIndicator] = useState({ left: 0, width: 0, visible: false });
  const navItemRefs = useRef([]);

  const activeIndex = navItems.findIndex((item) => item.path === location.pathname);
  const indicatorIndex = hoveredIndex ?? (activeIndex >= 0 ? activeIndex : null);

  const scrollTrigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 50,
  });

  const scrolled = scrollTrigger;
  const isLight = transparent && !scrolled;

  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  // Label widths shift with language and with webfont loading, so re-measure on both.
  useLayoutEffect(() => {
    if (isMobile) return undefined;

    const measure = () => {
      const el = indicatorIndex != null ? navItemRefs.current[indicatorIndex] : null;

      if (!el) {
        setIndicator((prev) => ({ ...prev, visible: false }));
        return;
      }

      setIndicator({ left: el.offsetLeft, width: el.offsetWidth, visible: true });
    };

    measure();
    window.addEventListener('resize', measure);
    document.fonts?.ready.then(measure).catch(() => {});

    return () => window.removeEventListener('resize', measure);
  }, [indicatorIndex, isMobile, i18n.language]);

  const toggleDrawer = (open) => () => {
    setDrawerOpen(open);
  };

  return (
    <>
      <StyledAppBar scrolled={scrolled} transparent={transparent}>
        <Container maxWidth="xl">
          <Toolbar disableGutters sx={{ justifyContent: 'space-between' }}>
            {/* Logo */}
            <Logo component={Link} to="/">
              <img
                src="/logo.png"
                alt="Nexfah"
                style={{
                  filter: isLight ? 'brightness(0) invert(1)' : 'none',
                }}
              />
            </Logo>

            {/* Desktop Navigation */}
            {!isMobile && (
              <NavRail
                component="nav"
                light={isLight ? 1 : 0}
                onMouseLeave={() => setHoveredIndex(null)}
              >
                <NavIndicator
                  light={isLight ? 1 : 0}
                  visible={indicator.visible ? 1 : 0}
                  sx={{
                    width: `${indicator.width}px`,
                    transform: `translateX(${indicator.left}px)`,
                  }}
                />
                {navItems.map((item, index) => (
                  <NavLink
                    key={item.path}
                    ref={(el) => { navItemRefs.current[index] = el; }}
                    component={Link}
                    to={item.path}
                    active={location.pathname === item.path ? 1 : 0}
                    light={isLight ? 1 : 0}
                    aria-current={location.pathname === item.path ? 'page' : undefined}
                    disableRipple
                    onMouseEnter={() => setHoveredIndex(index)}
                    onFocus={() => setHoveredIndex(index)}
                    onBlur={() => setHoveredIndex(null)}
                  >
                    {t(item.label)}
                  </NavLink>
                ))}
              </NavRail>
            )}

            {/* Right side actions */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <LanguageSwitcher light={isLight} />

              {!isMobile && (
                <GradientButton
                  component={Link}
                  to="/contact"
                  size="small"
                >
                  {t('nav.getInTouch')}
                </GradientButton>
              )}

              {isMobile && (
                <IconButton
                  onClick={toggleDrawer(true)}
                  sx={{ color: isLight ? '#FFFFFF' : 'text.primary' }}
                  aria-label="Open menu"
                >
                  <MenuIcon />
                </IconButton>
              )}
            </Box>
          </Toolbar>
        </Container>
      </StyledAppBar>

      {/* Mobile Drawer */}
      <MobileDrawer
        anchor="right"
        open={drawerOpen}
        onClose={toggleDrawer(false)}
      >
        <DrawerHeader>
          <Logo component={Link} to="/">
            <img src="/logo.png" alt="Nexfah" style={{ height: '36px' }} />
          </Logo>
          <IconButton onClick={toggleDrawer(false)} aria-label="Close menu">
            <CloseIcon />
          </IconButton>
        </DrawerHeader>

        <List>
          {navItems.map((item) => (
            <ListItem key={item.path} disablePadding>
              <ListItemButton
                component={Link}
                to={item.path}
                selected={location.pathname === item.path}
                aria-current={location.pathname === item.path ? 'page' : undefined}
                sx={{
                  position: 'relative',
                  borderRadius: '12px',
                  mb: 0.5,
                  py: 1.25,
                  pl: 2.5,
                  color: 'text.secondary',

                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: '3px',
                    height: location.pathname === item.path ? '20px' : '0px',
                    borderRadius: '0 3px 3px 0',
                    background: theme.palette.accent.gradient,
                    transition: 'height 0.25s ease',
                  },

                  '&.Mui-selected': {
                    backgroundColor: 'rgba(0, 184, 212, 0.08)',
                    color: 'primary.main',
                  },
                }}
              >
                <ListItemText
                  primary={t(item.label)}
                  primaryTypographyProps={{
                    fontFamily: '"Plus Jakarta Sans", sans-serif',
                    fontSize: '1.0625rem',
                    letterSpacing: '-0.01em',
                    fontWeight: location.pathname === item.path ? 700 : 600,
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>

        <Box sx={{ mt: 'auto', pt: 3 }}>
          <GradientButton
            component={Link}
            to="/contact"
            fullWidth
            size="large"
          >
            {t('nav.getInTouch')}
          </GradientButton>
        </Box>
      </MobileDrawer>

      {/* Spacer for fixed header */}
      {!transparent && <Toolbar />}
    </>
  );
}
